import { Component } from '@angular/core';
import { EventType, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/services/user/auth.service';
import { CurrentUserService } from '../../core/services/user/current-user.service';
import { CommonModule } from '@angular/common';
import { environment } from '../../../environments/environment';
import { StatusUtils } from '../../utils/status-utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CookieService } from '../../core/services/cookie.service';
import { UserSessionModel } from '../../core/models/user/current-user.model';
import { Subscription } from 'rxjs';
import { UsersService } from '../../core/services/user/users.service';
import { UserRole } from '../../../enums/roles.enum';
import { SidebarItems } from '../../core/models/sidebar/sidebar-items.model';
declare var KTComponents: any;

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule, TranslateModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private subscription: Subscription;
  constructor(
    private authService: AuthService,
    private currentUserService: CurrentUserService,
    private translate: TranslateService,
    private cookieService: CookieService,
    private router: Router,
    private userService: UsersService
  ) {
    this.routerSubscribe();
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    this.initializeMenu();
    this.translate.onLangChange.subscribe(() => {
      this.initializeMenu();
      this.filterSidebarItems();
    });
  }
  sidebarItems: SidebarItems = {
    top: [],
    bottom: [],
  };
  filteredSidebarItems: SidebarItems = {
    top: [],
    bottom: [],
  };
  currentUser: UserSessionModel = null;
  isSingleApplication: boolean;
  userAvatar: string | null = null;
  userRole = UserRole;

  ngAfterViewInit() {
    setTimeout(() => {
      KTComponents?.init();
    }, 200);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async ngOnInit() {
    await this.getUserAvatar();
    await StatusUtils?.initializeTranslateService(this.translate);
    this.currentUser = this.currentUserService.GetSession();
    this.isSingleApplication =
      this.cookieService.getCookie('applications') != null
        ? JSON.parse(this.cookieService.getCookie('applications')).length == 1
        : true;
    this.filterSidebarItems();
  }

  filterSidebarItems() {
    this.filteredSidebarItems.top = this.sidebarItems.top.filter(
      (item) =>
        !item.authorizations.length ||
        item.authorizations.includes(this.currentUser?.role)
    );
    this.filteredSidebarItems.bottom = this.sidebarItems.bottom.filter(
      (item) =>
        !item.authorizations.length ||
        item.authorizations.includes(this.currentUser?.role)
    );
  }

  initializeMenu() {
    this.sidebarItems.top = [
      {
        title: this.translate.instant('dashboard.dashboardMenuTitle'),
        value: 'dashboard',
        icon: 'fa-solid fa-clipboard-list',
        route: '/dashboard/main',
        authorizations: [],
        subItems: [],
      },
      {
        title: this.translate.instant('users'),
        value: 'users',
        icon: 'fa-solid fa-user',
        route: '/user/list',
        authorizations: [this.userRole.Admin, this.userRole.OrganizationAdmin],
        subItems: [
          {
            title: this.translate.instant('userList'),
            route: '/user/list',
          },
          {
            title: this.translate.instant('createUser'),
            route: '/user/create',
          },
        ],
      },
      {
        title: this.translate.instant('consultations'),
        value: 'consultations',
        icon: 'fa-solid fa-file-lines',
        route: '/consultation/list',
        authorizations: [
          this.userRole.Dermatologist,
          this.userRole.Admin,
          this.userRole.OrganizationAdmin,
        ],
        subItems: [],
      },
      {
        title: this.translate.instant('appoinmentList'),
        value: 'appoinmentList',
        icon: 'fa-regular fa-rectangle-list',
        route: '/appointment/list',
        authorizations: [
          this.userRole.GeneralPractitioner,
          this.userRole.Assistant,
          this.userRole.Admin,
          this.userRole.OrganizationAdmin,
        ],
        subItems: [],
      },
      {
        title: this.translate.instant('patientList'),
        value: 'patientList',
        icon: 'fa-solid fa-users',
        route: '/patient/list',
        authorizations: [
          this.userRole.GeneralPractitioner,
          this.userRole.Assistant,
          this.userRole.Admin,
          this.userRole.OrganizationAdmin,
        ],
        subItems: [],
      },
      {
        title: 'DermoScan',
        value: 'dermoscan',
        icon: 'fa-solid fa-mobile-screen-button',
        route: '/dermoscan/list',
        authorizations: [
          this.userRole.Dermatologist,
          this.userRole.GeneralPractitioner,
          this.userRole.Assistant,
          this.userRole.Admin,
          this.userRole.OrganizationAdmin,
        ],
        subItems: [],
      },
      {
        title: this.translate.instant('referralList'),
        value: 'referralList',
        icon: 'fa-solid fa-list-check',
        route: '/referral/list',
        authorizations: [
          this.userRole.Admin,
          this.userRole.GeneralPractitioner,
          this.userRole.OrganizationAdmin,
        ],
        subItems: [],
      },
      {
        title: this.translate.instant('invoiceList'),
        value: 'invoiceList',
        icon: 'fa-solid fa-file-invoice',
        route: '/invoice/list',
        authorizations: [
          this.userRole.Admin,
          this.userRole.GeneralPractitioner,
          this.userRole.OrganizationAdmin,
        ],
        subItems: [],
      },
    ];
    this.sidebarItems.bottom = [
      {
        title: this.translate.instant('profile'),
        value: 'profile',
        icon: 'fa-regular fa-user',
        route: '/profile/detail',
        authorizations: [],
        subItems: [],
      },
      {
        title: this.translate.instant('settings'),
        value: 'settings',
        icon: 'fa-solid fa-gear',
        route: '',
        authorizations: [this.userRole.Admin],
        subItems: [],
        onClick: () => this.navigateToSettings(),
      },
      {
        title: this.translate.instant('info'),
        value: 'info',
        icon: 'fa-solid fa-circle-info info',
        route: '/profile/info',
        authorizations: [],
        subItems: [],
      },
      {
        title: this.translate.instant('logout'),
        value: 'logout',
        icon: 'fa fa-sign-out',
        route: '',
        authorizations: [],
        subItems: [],
        onClick: () => this.signOut(),
      },
    ];

    const menuItems = document.querySelectorAll(
      '[data-kt-menu-trigger="click"]'
    );
    menuItems.forEach((item) => {
      const menuLink = item.querySelector('.menu-link') as HTMLElement;
      const menuSub = item.querySelector('.menu-sub') as HTMLElement;

      if (menuLink) {
        menuLink.addEventListener('click', (event) => {
          event.stopPropagation();
          event.preventDefault();
          if (menuSub) {
            if (menuSub.style.display === 'block') {
              menuSub.style.display = 'none';
            } else {
              const openMenus = document.querySelectorAll('.menu-sub');
              openMenus.forEach((openMenu) => {
                (openMenu as HTMLElement).style.display = 'none';
              });
              menuSub.style.display = 'block';
            }
          }
        });
      }

      if (menuSub) {
        const subMenuLinks = menuSub.querySelectorAll('.menu-item a');
        subMenuLinks.forEach((subMenuLink) => {
          subMenuLink.addEventListener('click', (event) => {
            event.stopPropagation();
          });
        });
      }
    });
  }

  routerSubscribe() {
    this.subscription?.unsubscribe();
    this.subscription = this.router.events.subscribe((event) => {
      if (
        event.type === EventType.NavigationEnd ||
        event.constructor.name === 'NavigationEnd'
      ) {
        this.closeSidebar(event['url']);
      }
    });
  }

  async getUserAvatar() {
    await this.userService.getUserAvatar();
    this.userAvatar = this.userService?.userAvatar
      ? `data:image/jpeg;base64,${this.userService?.userAvatar}`
      : 'assets/media/avatars/profile.png';
  }

  closeSidebar(url: string) {
    const closeList = ['/consultation/detail'];
    const isClosed = document.body.hasAttribute('data-kt-app-sidebar-minimize');
    if (isClosed || !url || typeof url !== 'string') return;
    const hasRoute = closeList.find((x) => !!url.includes(x));
    if (!hasRoute) return;
    const toggleBtnElement = document.getElementById('kt_app_sidebar_toggle');
    toggleBtnElement?.click();
  }

  navigateToSettings() {
    if (typeof window !== 'undefined') {
      window.location.href = `${environment.apiLocations.backOffice}`;
    }
  }

  signOut() {
    this.authService.logout().subscribe(() => {
      localStorage.setItem('isLogin', 'false');
      if (this.userService?.userAvatar !== undefined) {
        this.userService.userAvatar = null;
      }
    });
  }
}
